import React, { useEffect } from 'react';
import { Modal, TextField, MenuItem, FormControl } from '@mui/material';
import { FormInputText } from '../../../../form-components/FormInputText';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '../../../CalendarIcon';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { useToast } from 'use-toast-mui';
import { errorMessages, validateDateRange } from '../../../../utils/methods';
import {
  stopLockerPassById,
  stopCancelLockerPassById,
  stopProductPassById,
  stopCancelProductPassById,
  modifyStopLockerPassById,
  modifyStopProductPassById,
} from '../../../../api/member-management';
import { getAllStaffs } from '../../../../api/staff';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  FormSection,
  FormRow,
  FormLabel,
  DatePickerWrapper,
  DatePickerSeparator,
  SmallInputWrapper,
  SmallTextField,
  InfoDisplayWrapper,
  InfoDateText,
  InfoDaysWrapper,
  InfoDaysNumber,
  InfoDaysText,
  ButtonWrapper,
  CancelButton,
  ReleaseButton,
  SaveButton,
  StyledSelect,
} from '../../ChangeTFreezyModalStyled';

// 라커/일반상품(운동복 등) 중지 설정 모달 — 수강권 중지 모달(ChangeTFreezyModal)의 UI/스타일과
// 저장·해제 버튼 로직을 그대로 따르되, open/onClose props 계약은 ChangeGeneralProductPeriodModal과 통일한다.
export default function ChangeGeneralProductStopModal({ open, onClose, ticket, memberId, getAllUserTickets }) {
  const { t } = useTranslation();
  const toast = useToast();
  const bigUser = useSelector(state => state.user.user);
  const branchId = bigUser?.branch?.branchId;
  const headBranchId = bigUser?.headBranch?.headBranchId;

  // 락커인지 일반상품인지 구분 — 라커는 그룹 대표 row가 만료건일 수 있어 중지 대상 passId는 stopTargetPassId를 써야 한다.
  const isLocker = !!ticket?.memberLockerPassId;
  const passId = isLocker ? ticket?.stopTargetPassId : ticket?.memberProductPassId;

  const { data: staffData } = useQuery({
    queryKey: ['staffList', headBranchId, branchId],
    queryFn: () => getAllStaffs(bigUser.token, `headBranchId=${headBranchId}&branchId=${branchId}&page=1&limit=100`),
    enabled: !!bigUser?.token && !!branchId && open,
  });
  const staffList = staffData?.content ?? [];

  // stopCount가 null이면 무제한, 0이면 중지 불가. stopPeriod(잔여 일수)도 null이면 무제한.
  const isUnlimited = ticket?.stopCount === null;
  const isPeriodUnlimited = ticket?.stopPeriod === null || ticket?.stopPeriod === undefined;

  const { handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      stopTimes: '',
      startDate: ticket?.stoppedDate ?? null,
      endDate: ticket?.stoppedEndDate ?? null,
      managerId: '',
      memo: '',
    },
  });

  // 카드가 바뀌면(다른 라커/상품 선택) 폼 값도 새 ticket 기준으로 초기화.
  useEffect(() => {
    reset({
      stopTimes: '',
      startDate: ticket?.stoppedDate ?? null,
      endDate: ticket?.stoppedEndDate ?? null,
      managerId: '',
      memo: '',
    });
  }, [ticket, reset]);

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');
  const watchedStopTimes = watch('stopTimes');
  const watchedManagerId = watch('managerId');

  // STOPPED = 이미 중지된 상품의 중지기간 수정 모드 (중지 횟수 재차감 없음, 백엔드가 잔여일수 검증)
  const isStopped = ticket?.status === 'STOPPED';
  const isActive =
    isStopped || (ticket?.status === 'ACTIVE' && (isUnlimited || Number(ticket?.stopCount) >= 1));
  const canRelease = ticket?.status !== 'ACTIVE';

  // 중지기간 수정 시 종료일을 과거로 당기지 못하게 오늘을 하한으로 둔다 (시작일이 미래면 시작일이 하한)
  const endDatePickerMinDate = isStopped
    ? watchedStartDate && moment(watchedStartDate).isAfter(moment(), 'day')
      ? watchedStartDate
      : moment().startOf('day').toDate()
    : watchedStartDate || null;

  const handleClose = () => {
    reset();
    onClose();
  };

  const cancelStopByMemberId = async () => {
    if (isLocker && !passId) {
      toast.error('중지 대상 라커 정보를 찾을 수 없습니다');
      return;
    }

    try {
      const managerId = watch('managerId');
      const memo = watch('memo');
      const data = { managerId: managerId || null, memo: memo || null };

      if (isLocker) {
        await stopCancelLockerPassById(bigUser.token, memberId, passId, data);
      } else {
        await stopCancelProductPassById(bigUser.token, memberId, passId, data);
      }
      toast.success(t('Successfully saved'));
      getAllUserTickets();
      handleClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const stopByMemberId = async values => {
    const { stopTimes, startDate, endDate, managerId, memo } = values;

    if (!startDate) {
      toast.error('중지 시작 날짜를 선택해주세요');
      return;
    }

    if (!endDate) {
      toast.error('중지 종료 날짜를 선택해주세요');
      return;
    }

    if (!validateDateRange(startDate, endDate)) {
      toast.error('기간을 다시 확인해주세요');
      return;
    }

    // 중지기간 수정 시 종료일을 과거로 당기는 것은 막는다 (오늘 이후로만)
    if (isStopped && moment(endDate).isBefore(moment().startOf('day'))) {
      toast.error('중지 종료일은 오늘 이후로만 변경할 수 있습니다');
      return;
    }

    if (isLocker && !passId) {
      toast.error('중지 대상 라커 정보를 찾을 수 없습니다');
      return;
    }

    try {
      const data = {
        stopPeriod: Number(stopTimes),
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        managerId: managerId || null,
        memo: memo || null,
      };

      // 이미 중지된 상품은 중지기간 수정(PUT), 아니면 새 중지(POST)
      if (isLocker) {
        if (isStopped) {
          await modifyStopLockerPassById(bigUser.token, memberId, passId, data);
        } else {
          await stopLockerPassById(bigUser.token, memberId, passId, data);
        }
      } else if (isStopped) {
        await modifyStopProductPassById(bigUser.token, memberId, passId, data);
      } else {
        await stopProductPassById(bigUser.token, memberId, passId, data);
      }
      toast.success(t('Successfully saved'));
      getAllUserTickets();
      handleClose();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    const startDate = watch('startDate');
    const endDate = watch('endDate');

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const timeDifference = end.getTime() - start.getTime();
      const dayDifference = Math.ceil(timeDifference / (1000 * 3600 * 24)) + 1;

      setValue('stopTimes', dayDifference);
    }
  }, [watch('startDate'), watch('endDate'), setValue]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="product-stop-modal-title"
      aria-describedby="product-stop-modal-description">
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>중지설정</ModalTitle>
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <FormSection>
            {/* 중지 기간 */}
            <FormRow>
              <FormLabel>중지 기간</FormLabel>
              <DatePickerWrapper>
                <FormInputDate
                  name="startDate"
                  control={control}
                  placeholder="YYYY-MM-DD"
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={params => (
                    <TextField
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          borderRadius: '8px',
                        },
                        '& .MuiSvgIcon-root': {
                          fill: '#0F7F85 !important',
                        },
                      }}
                      size="small"
                      {...params}
                    />
                  )}
                />
                <DatePickerSeparator>~</DatePickerSeparator>
                <FormInputDate
                  name="endDate"
                  control={control}
                  placeholder="YYYY-MM-DD"
                  minDate={endDatePickerMinDate}
                  disabled={!watchedStartDate}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                  renderInput={params => (
                    <TextField
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          height: '44px',
                          borderRadius: '8px',
                        },
                        '& .MuiSvgIcon-root': {
                          fill: '#0F7F85 !important',
                        },
                      }}
                      size="small"
                      {...params}
                    />
                  )}
                />
              </DatePickerWrapper>
            </FormRow>

            {/* 중지 가능 횟수 — 별도 변경 API가 없어 조회 전용으로 표시(무제한/0회=중지불가 안내). */}
            <FormRow>
              <FormLabel>중지 가능 횟수</FormLabel>
              <SmallInputWrapper>
                <SmallTextField
                  type="text"
                  size="small"
                  value={isUnlimited ? '무제한' : `${Math.max(0, ticket?.stopCount ?? 0)}회`}
                  disabled
                />
              </SmallInputWrapper>
            </FormRow>

            {/* 중지 가능 일수 — 요청 일수가 이 값을 넘으면 저장 시 거부된다. 조회 전용. */}
            <FormRow>
              <FormLabel>중지 가능 일수</FormLabel>
              <SmallInputWrapper>
                <SmallTextField
                  type="text"
                  size="small"
                  value={isPeriodUnlimited ? '무제한' : `${Math.max(0, ticket?.stopPeriod ?? 0)}일`}
                  disabled
                />
              </SmallInputWrapper>
            </FormRow>

            {/* 관리 담당자 */}
            <FormRow>
              <FormLabel>관리 담당자</FormLabel>
              <FormControl sx={{ width: '163px' }} size="small">
                <StyledSelect
                  value={watchedManagerId}
                  onChange={e => setValue('managerId', e.target.value)}
                  displayEmpty>
                  <MenuItem value="">
                    <em>선택</em>
                  </MenuItem>
                  {staffList.map(staff => (
                    <MenuItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </FormRow>

            {/* 메모 */}
            <FormRow>
              <FormLabel>메모</FormLabel>
              <FormInputText
                name="memo"
                control={control}
                placeholder="메모를 입력해 주세요"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    height: '44px',
                    borderRadius: '8px',
                  },
                }}
              />
            </FormRow>

            {/* 중지일 안내 */}
            <FormRow>
              <FormLabel>중지일 안내</FormLabel>
              <InfoDisplayWrapper>
                <InfoDateText isPlaceholder={!watchedStartDate || !watchedEndDate}>
                  {watchedStartDate ? moment(watchedStartDate).format('YYYY-MM-DD') : 'YYYY-MM-DD'} ~{' '}
                  {watchedEndDate ? moment(watchedEndDate).format('YYYY-MM-DD') : 'YYYY-MM-DD'}
                </InfoDateText>
                <InfoDaysWrapper>
                  <InfoDaysNumber>{watchedStopTimes || 0}</InfoDaysNumber>
                  <InfoDaysText>일간</InfoDaysText>
                </InfoDaysWrapper>
              </InfoDisplayWrapper>
            </FormRow>
          </FormSection>

          <ButtonWrapper>
            <CancelButton onClick={handleClose}>취소</CancelButton>
            <ReleaseButton isActive={canRelease} disabled={!canRelease} onClick={cancelStopByMemberId}>
              해제
            </ReleaseButton>
            <SaveButton isActive={isActive} disabled={!isActive} onClick={handleSubmit(stopByMemberId)}>
              저장
            </SaveButton>
          </ButtonWrapper>
        </ModalContent>
      </ModalContainer>
    </Modal>
  );
}
